import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  getDefaultLayoutForUser,
  LAYOUT_VERSION,
} from "./defaultLayout";

// Storage path: users/{uid}/preferences/dashboard
// Single-doc model -- the entire layout for a user lives in one small
// record. setDoc with merge:true so future fields (sidebarOrder when it
// lands in Phase 5, presets, etc.) can be added without rewriting the
// quadrants block.
const dashboardPrefRef = (db, uid) =>
  doc(db, "users", uid, "preferences", "dashboard");

// Returns whatever the user has saved, or the role/department default
// if they've never customized. Lazy migration: nothing is written to
// Firestore on first load -- we only write when the user explicitly
// customizes their layout (Phase 4 edit UI).
export async function loadLayout(db, uid, role, department) {
  if (!uid) return getDefaultLayoutForUser(role, department);
  try {
    const snap = await getDoc(dashboardPrefRef(db, uid));
    if (snap.exists()) {
      const data = snap.data();
      // Older saved layouts that predate the current LAYOUT_VERSION
      // could theoretically need migration. We don't have any yet --
      // the version check is a hook for the future, not active logic.
      if (data?.version === LAYOUT_VERSION) return data;
    }
  } catch (err) {
    // Read failures (offline, permission, etc.) fall through to the
    // default so the dashboard always renders. The user can retry by
    // customizing -- the write path surfaces its own errors.
    console.warn("loadLayout: falling back to default", err);
  }
  return getDefaultLayoutForUser(role, department);
}

// Persists the layout. Phase 4 calls this from the edit UI; Phase 1
// just exports it so the wiring is ready when Phase 4 lands.
export async function saveLayout(db, uid, layout) {
  if (!uid) throw new Error("saveLayout: uid is required");
  await setDoc(
    dashboardPrefRef(db, uid),
    { ...layout, version: LAYOUT_VERSION },
    { merge: true }
  );
}
